/**
 * Widgets of the access-level dashboards (Beta-1 plan P3-10 / P3-13 close, B4): Individual
 * Contributor, FLS, Department Head and Executive. Each page keeps its hero and any fixed banner,
 * assembles `useRoleDashboardData()` + `useOEDashboardData()` and renders a CustomizableDashboard;
 * these widgets read both contexts and fetch nothing themselves. Importing registers them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useRoleDashboard, RoleDashboardProvider } from './RoleDashboardContext.jsx';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, LinearProgress, List, ListItem,
  ListItemIcon, ListItemText, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon, Block as BlockIcon, CheckCircleOutline as CheckCircleOutlineIcon, Flag as FlagIcon,
  TrendingDown as TrendingDownIcon, TrendingFlat as TrendingFlatIcon, TrendingUp as TrendingUpIcon,
  VisibilityOff as VisibilityOffIcon, WarningAmber as WarningAmberIcon, Assessment as ReportIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemoRole } from '../../../contexts/DemoRoleContext';
import { useUserOrganizations } from '../hooks/useOrganization';
import { useSiteComparisons } from '../hooks/useSiteComparisons';
import { useTRLs } from '../hooks/useTRLs';
import { useOwnedTRLs, mergeConditions } from '../hooks/useOwnedTRLs';
import { useConditionViewer } from '../hooks/useConditionViewer';
import { useRiskAlerts } from '../hooks/useRiskAlerts';
import { SEVERITY_LEVELS, conditionStatusLabel } from '../utils/trlConstants';
import { REPS_COLORS, oeBand } from '../utils/constants';
import { timestampToDate } from '../../../utils/firestore';
import { widgetRegistry, DASHBOARD_KEYS } from '../../../platform/widgets/widgetRegistry.js';

const ROOT = '/drew';
const K = DASHBOARD_KEYS;

/* ------------------------------------------------------------------ context */

export { useRoleDashboard, RoleDashboardProvider };

/**
 * Everything the role widgets read: persona lock, org units, comparisons (headcount-weighted,
 * P2-38), the persona-scoped conditions + the ones they own, risk alerts, health messages.
 */
export function useRoleDashboardData({ roleKey }) {
  const { user } = useAuth();
  const { persona, personaUser, queryScope } = useDemoRole();
  const { organizations } = useUserOrganizations(user?.uid);
  const activeOrg = organizations[0] || null;
  const thresholds = activeOrg?.thresholds || null;
  const { comparisons, loading: compLoading } = useSiteComparisons(activeOrg?.id || null);
  const { alerts: riskAlerts } = useRiskAlerts(activeOrg?.id || null, user?.uid || null);

  const myNwt = activeOrg?.teams?.find((t) => t.id === queryScope?.nwtId) || null;
  const myDept =
    (myNwt ? activeOrg?.departments?.find((d) => d.id === myNwt.departmentId) : null) ||
    activeOrg?.departments?.find((d) => d.id === queryScope?.deptId) ||
    null;
  const mySite =
    (myDept ? activeOrg?.sites?.find((s) => s.id === myDept.siteId) : null) ||
    activeOrg?.sites?.find((s) => s.id === queryScope?.siteId) ||
    null;

  const { trls, loading: trlsLoading } = useTRLs({ limit: 300 });
  const viewer = useConditionViewer();
  const owned = useOwnedTRLs(viewer);
  const mergedConditions = useMemo(() => mergeConditions(trls, owned), [trls, owned]);

  // Health messages (Divergence Detection output) — executives only; one listener, org-fenced.
  const [healthMessages, setHealthMessages] = useState([]);
  const wantsHealth = roleKey === K.drewExec;
  useEffect(() => {
    if (!wantsHealth || !activeOrg?.id) {
      setHealthMessages([]);
      return undefined;
    }
    const q = query(collection(db, 'Hugo_HealthMessages'), where('organizationId', '==', activeOrg.id));
    return onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const m = { id: d.id, ...d.data() };
          const t = m.triggeredAt;
          return { ...m, triggeredAt: t?.toDate ? t.toDate() : t };
        });
        msgs.sort((a, b) => new Date(b.triggeredAt) - new Date(a.triggeredAt));
        setHealthMessages(msgs);
      },
      () => setHealthMessages([])
    );
  }, [wantsHealth, activeOrg?.id]);

  const teamRow = comparisons?.teamScores?.find((t) => t.teamId === myNwt?.id) || null;
  const deptRow = comparisons?.deptScores?.find((d) => d.deptId === myDept?.id) || null;
  const siteRow = comparisons?.siteScores?.find((s) => s.siteId === mySite?.id) || null;

  return useMemo(
    () => ({
      roleKey,
      user,
      persona,
      personaUser,
      queryScope,
      activeOrg,
      thresholds,
      comparisons,
      compLoading,
      myNwt,
      myDept,
      mySite,
      teamRow,
      deptRow,
      siteRow,
      trls,
      trlsLoading,
      mergedConditions,
      riskAlerts,
      healthMessages,
      teamName: (teamId) => activeOrg?.teams?.find((t) => t.id === teamId)?.name || teamId,
    }),
    [roleKey, user, persona, personaUser, queryScope, activeOrg, thresholds, comparisons, compLoading, myNwt, myDept, mySite, teamRow, deptRow, siteRow, trls, trlsLoading, mergedConditions, riskAlerts, healthMessages]
  );
}

/** The OE-dashboard scope a persona lock maps to (team → department → site → organization). */
export function scopeForLock(queryScope) {
  if (queryScope?.nwtId) return { type: 'team', id: queryScope.nwtId };
  if (queryScope?.deptId) return { type: 'department', id: queryScope.deptId };
  if (queryScope?.siteId) return { type: 'site', id: queryScope.siteId };
  return { type: 'organization', id: null };
}

/* ------------------------------------------------------------------ shared bits */

const sevColor = (sev) => SEVERITY_LEVELS.find((s) => s.value === sev)?.color || '#999';
const typeIcon = (type) => {
  if (type === 'trap') return <WarningAmberIcon fontSize="small" />;
  if (type === 'roadblock') return <BlockIcon fontSize="small" />;
  return <VisibilityOffIcon fontSize="small" />;
};

function Shell({ title, overline, action, children, scroll = true }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pb: 1.5 }}>
        {overline && <Typography variant="overline" color="text.secondary">{overline}</Typography>}
        {(title || action) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" noWrap>{title}</Typography>
            {action}
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: scroll ? 'auto' : 'hidden' }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

/** Centered empty state — a quiet check mark and one line, so an empty card reads as "all clear"
 *  instead of a blank box (UI pass 2026-09-21). */
function EmptyState({ text, icon: Icon = CheckCircleOutlineIcon }) {
  return (
    <Box sx={{ height: '100%', minHeight: 96, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: 'text.secondary', textAlign: 'center', py: 2 }}>
      <Icon sx={{ fontSize: 30, opacity: 0.5 }} />
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
}

function ScoreBars({ scores, thresholds, dense = false }) {
  return (
    <Stack spacing={dense ? 1 : 1.5}>
      {Object.entries(scores).map(([key, val]) => {
        const band = oeBand(val, thresholds, key);
        return (
          <Box key={key}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ textTransform: 'capitalize', color: REPS_COLORS[key], fontWeight: 700 }}>{key}</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: band.color }}>{Number(val).toFixed(1)} / 10</Typography>
            </Box>
            <LinearProgress variant="determinate" value={val * 10} sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { backgroundColor: band.color } }} />
          </Box>
        );
      })}
    </Stack>
  );
}

function ScoreTiles({ scores, thresholds, big = false }) {
  return (
    <Grid container spacing={1.5}>
      {Object.entries(scores).map(([key, val]) => {
        const band = oeBand(val, thresholds, key);
        return (
          <Grid item xs={6} sm={3} key={key}>
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'capitalize', color: REPS_COLORS[key], fontWeight: 700 }}>{key}</Typography>
              <Typography variant={big ? 'h4' : 'h5'} fontWeight={700} sx={{ color: band.color }}>{Number(val).toFixed(1)}</Typography>
              <LinearProgress variant="determinate" value={val * 10} sx={{ height: 4, borderRadius: 2, mt: 0.5, '& .MuiLinearProgress-bar': { backgroundColor: band.color } }} />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

const Spinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress size={28} />
  </Box>
);

/* ------------------------------------------------------------------ ITM: my submissions */

function MySubmissionsWidget({ size }) {
  const ctx = useRoleDashboard();
  const list = (ctx?.trls || []).slice(0, size === 'M' ? 4 : size === 'L' ? 8 : 15);
  return (
    <Shell title="My Recent Submissions">
      {ctx?.trlsLoading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No OE Conditions in your scope yet. Click &quot;Log an OE Condition&quot; above to submit your first one.
        </Typography>
      ) : (
        <List disablePadding dense>
          {list.map((trl, idx) => (
            <React.Fragment key={trl.id}>
              <ListItem disableGutters sx={{ py: 0.75, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 32, mt: 0.5, color: sevColor(trl.severityITM) }}>{typeIcon(trl.type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>{trl.title}</Typography>
                      <Chip label={`Sev ${trl.severityITM}`} size="small" sx={{ bgcolor: sevColor(trl.severityITM), color: '#fff', fontSize: '0.65rem', height: 18 }} />
                      <Chip label={conditionStatusLabel(trl.status)} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {String(trl.type || '').toUpperCase()} • {timestampToDate(trl.submittedAt)?.toLocaleDateString() || '—'}
                      {trl.aiAnalysis && ' • AI ✓'}
                    </Typography>
                  }
                />
              </ListItem>
              {idx < list.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Shell>
  );
}

/* ------------------------------------------------------------------ my NWT / my site / my department */

function MyNwtScoresWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { teamRow, comparisons, compLoading, thresholds, myNwt, trls } = ctx;
  const nwtScores = teamRow
    ? { scores: teamRow.scores, responseCount: teamRow.responseCount, lowConfidence: teamRow.lowConfidence, basis: teamRow.basis }
    : comparisons?.enterpriseAverage
      ? { scores: comparisons.enterpriseAverage, responseCount: null }
      : null;
  const fallback = !teamRow && Boolean(nwtScores);
  const pending = trls.filter((t) => t.status === 'submitted' || t.status === 'review_l1').length;
  const inReview = trls.filter((t) => t.status === 'under_review').length;
  const approved = trls.filter((t) => t.status === 'approved').length;
  return (
    <Shell overline="My NWT" title={myNwt?.name || 'My Team'}>
      {compLoading && <Spinner />}
      {!compLoading && !nwtScores && <Alert severity="info">No survey data yet for your organization — scores appear once responses come in.</Alert>}
      {!compLoading && fallback && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Showing the organization-wide average — team-level scores appear once your team has recent survey responses.
        </Typography>
      )}
      {!compLoading && nwtScores && <ScoreBars scores={nwtScores.scores} thresholds={thresholds} />}
      {!compLoading && nwtScores && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={nwtScores.responseCount != null ? `${nwtScores.basis || `${nwtScores.responseCount} responses`} (14d)${nwtScores.lowConfidence ? ' · low confidence' : ''}` : 'Org-wide average'}
            size="small"
            variant="outlined"
            color={nwtScores.lowConfidence ? 'warning' : 'default'}
          />
          <Chip label={`${pending} pending`} size="small" color="warning" variant="outlined" />
          <Chip label={`${inReview} in review`} size="small" color="info" variant="outlined" />
          <Chip label={`${approved} approved`} size="small" color="success" variant="outlined" />
        </Box>
      )}
    </Shell>
  );
}

function MySiteScoresWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { siteRow, comparisons, compLoading, thresholds, mySite, activeOrg } = ctx;
  const siteScores = siteRow ? { scores: siteRow.scores } : comparisons?.enterpriseAverage ? { scores: comparisons.enterpriseAverage } : null;
  const fallback = !siteRow && Boolean(siteScores);
  return (
    <Shell overline="My Site" title={mySite?.name || activeOrg?.name || 'My Site'}>
      {compLoading && <Spinner />}
      {!compLoading && !siteScores && <Alert severity="info">No survey data yet for your organization — site scores appear once responses come in.</Alert>}
      {!compLoading && fallback && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Showing the organization-wide average — site-level scores appear once your site has survey snapshots.
        </Typography>
      )}
      {!compLoading && siteScores && <ScoreTiles scores={siteScores.scores} thresholds={thresholds} />}
      {siteRow?.basis && (
        <Typography variant="caption" color={siteRow.lowConfidence ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
          {siteRow.lowConfidence ? '⚠ ' : ''}{siteRow.basis}
        </Typography>
      )}
    </Shell>
  );
}

function DeptScoresWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { deptRow, compLoading, thresholds, myDept } = ctx;
  return (
    <Shell overline="Department REPS Scores" title={myDept?.name || 'My Department'}>
      {compLoading && <Spinner />}
      {!compLoading && !deptRow && <Alert severity="info">No recent survey responses for this department — scores appear once your teams respond to a survey.</Alert>}
      {!compLoading && deptRow && <ScoreTiles scores={deptRow.scores} thresholds={thresholds} big />}
      {deptRow?.basis && (
        <Typography variant="caption" color={deptRow.lowConfidence ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
          {deptRow.lowConfidence ? '⚠ ' : ''}{deptRow.basis}
        </Typography>
      )}
    </Shell>
  );
}

function NwtsInDeptWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { comparisons, compLoading, myDept, teamName } = ctx;
  const nwts = (comparisons?.teamScores || []).filter((s) => s.departmentId === myDept?.id);
  return (
    <Shell overline="NWTs in My Department" title={myDept?.name || 'Department'}>
      {compLoading && <Spinner />}
      {!compLoading && nwts.length === 0 && <Alert severity="info">No recent survey responses from teams in this department yet.</Alert>}
      <Stack spacing={1.5}>
        {!compLoading &&
          nwts.map((nwt) => (
            <Box key={nwt.teamId} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{teamName(nwt.teamId)}</Typography>
                <Chip label={nwt.basis || `${nwt.responseCount} responses`} size="small" variant="outlined" color={nwt.lowConfidence ? 'warning' : 'default'} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {Object.entries(nwt.scores).map(([k, v]) => (
                  <Typography key={k} variant="caption" color="text.secondary">
                    {k.charAt(0).toUpperCase()}: <strong>{Number(v).toFixed(1)}</strong>
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
      </Stack>
    </Shell>
  );
}

/* ------------------------------------------------------------------ queues */

function L1QueueWidget({ size }) {
  const ctx = useRoleDashboard();
  const navigate = useNavigate();
  const pending = (ctx?.trls || []).filter((t) => t.status === 'submitted' || t.status === 'review_l1');
  const rows = pending.slice(0, size === 'M' ? 4 : size === 'L' ? 8 : 20);
  return (
    <Shell
      title="Awaiting Your L1 Review"
      action={<Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`${ROOT}/rollup/review-queue`)}>Open Queue</Button>}
    >
      {ctx?.trlsLoading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : pending.length === 0 ? (
        <EmptyState text="Nothing waiting for your review." />
      ) : size === 'M' ? (
        <Stack spacing={0.75}>
          {rows.map((trl) => (
            <Box key={trl.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={trl.severityITM} size="small" sx={{ bgcolor: sevColor(trl.severityITM), color: '#fff', minWidth: 32 }} />
              <Typography variant="body2" noWrap sx={{ flex: 1 }}>{trl.title}</Typography>
            </Box>
          ))}
          {pending.length > rows.length && <Typography variant="caption" color="text.secondary">+{pending.length - rows.length} more</Typography>}
        </Stack>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Entrant Sev</TableCell>
              <TableCell align="center">AI Suggests</TableCell>
              <TableCell align="right">Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((trl) => (
              <TableRow key={trl.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`${ROOT}/rollup/review-queue`)}>
                <TableCell><Typography variant="body2" fontWeight={600}>{trl.title}</Typography></TableCell>
                <TableCell><Chip label={trl.type} size="small" variant="outlined" /></TableCell>
                <TableCell align="center"><Chip label={trl.severityITM} size="small" sx={{ bgcolor: sevColor(trl.severityITM), color: '#fff', minWidth: 36 }} /></TableCell>
                <TableCell align="center">
                  {trl.aiAnalysis?.suggestedSeverity ? (
                    <Chip label={trl.aiAnalysis.suggestedSeverity} size="small" sx={{ bgcolor: sevColor(trl.aiAnalysis.suggestedSeverity), color: '#fff', minWidth: 36 }} />
                  ) : (
                    <Chip label="…" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right"><Typography variant="caption" color="text.secondary">{timestampToDate(trl.submittedAt)?.toLocaleDateString() || '—'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}
    </Shell>
  );
}

function L2QueueWidget({ size }) {
  const ctx = useRoleDashboard();
  const navigate = useNavigate();
  const pending = (ctx?.trls || []).filter((t) => t.status === 'under_review' && !t.severityDH);
  const rows = pending.slice(0, size === 'M' ? 4 : size === 'L' ? 8 : 20);
  return (
    <Shell
      title="L2/DH Approval Queue"
      action={<Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`${ROOT}/rollup/severity-calibration`)}>Open Queue</Button>}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FlagIcon color="warning" fontSize="small" />
        {pending.length} item{pending.length !== 1 ? 's' : ''} from your NWTs awaiting your severity verification &amp; priority assignment.
      </Typography>
      <Stack spacing={1}>
        {rows.map((trl) => (
          <Box key={trl.id} sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate(`${ROOT}/rollup/severity-calibration`)}>
            <Chip label={`Sev ${trl.severityFLS || trl.severityITM}`} size="small" color="warning" />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{trl.title}</Typography>
              <Typography variant="caption" color="text.secondary">{trl.type} • NWT: {ctx?.teamName(trl.nwtId)}</Typography>
            </Box>
          </Box>
        ))}
        {pending.length === 0 && <EmptyState text="Nothing waiting for verification." />}
        {pending.length > rows.length && <Typography variant="caption" color="text.secondary">+{pending.length - rows.length} more in the queue</Typography>}
      </Stack>
    </Shell>
  );
}

/* ------------------------------------------------------------------ comparisons */

const toBars = (name, scores) => ({
  name,
  Reliability: scores.reliability,
  Efficiency: scores.efficiency,
  Productivity: scores.productivity,
  Safety: scores.safety,
});

function BarsChart({ data, height }) {
  return (
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 10]} />
          <Tooltip />
          <Legend />
          <Bar dataKey="Reliability" fill={REPS_COLORS.reliability} />
          <Bar dataKey="Efficiency" fill={REPS_COLORS.efficiency} />
          <Bar dataKey="Productivity" fill={REPS_COLORS.productivity} />
          <Bar dataKey="Safety" fill={REPS_COLORS.safety} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function ComparisonsWidget({ size }) {
  const ctx = useRoleDashboard();
  const navigate = useNavigate();
  if (!ctx) return null;
  const { comparisons, compLoading, myDept, teamName, roleKey } = ctx;
  const byNwt = roleKey === K.drewDh || roleKey === K.drewFls;
  const data = byNwt
    ? (comparisons?.teamScores || []).filter((s) => s.departmentId === myDept?.id).map((t) => toBars(teamName(t.teamId), t.scores))
    : (comparisons?.siteScores || []).map((s) => toBars(s.siteName.split(' ')[0], s.scores));
  return (
    <Shell
      title={byNwt ? 'NWT-to-NWT' : 'REPS Scores by Site'}
      action={<Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`${ROOT}/ia/comparisons`)}>Comparisons</Button>}
      scroll={false}
    >
      {compLoading && <Spinner />}
      {!compLoading && data.length === 0 && <Alert severity="info">{byNwt ? 'No recent survey responses from teams in this department yet.' : 'No site-level survey snapshots yet — the chart fills in as sites complete surveys.'}</Alert>}
      {!compLoading && data.length > 0 && <BarsChart data={data} height={size === 'XL' ? 300 : 200} />}
    </Shell>
  );
}

function SitesGlanceWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { comparisons, compLoading } = ctx;
  const sites = comparisons?.siteScores || [];
  const trendIcon = (t) => {
    if (t === 'improving') return <TrendingDownIcon fontSize="small" sx={{ color: '#2e7d32' }} />;
    if (t === 'worsening') return <TrendingUpIcon fontSize="small" sx={{ color: '#c62828' }} />;
    return <TrendingFlatIcon fontSize="small" sx={{ color: '#666' }} />;
  };
  return (
    <Shell title="Sites at a Glance">
      {compLoading && <Spinner />}
      {!compLoading && sites.length === 0 && <Alert severity="info">No site snapshots yet.</Alert>}
      <Stack spacing={1.25}>
        {sites.map((s) => {
          const avg = (s.scores.reliability + s.scores.efficiency + s.scores.productivity + s.scores.safety) / 4;
          return (
            <Box key={s.siteId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {trendIcon(s.trend)}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{s.siteName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Avg {avg.toFixed(1)} • {s.basis || `${s.lastSurveyResponses}/${s.targetResponses} responses`}{s.lowConfidence ? ' ⚠' : ''}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Shell>
  );
}

function EnterpriseScoresWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { comparisons, compLoading, thresholds } = ctx;
  const avg = comparisons?.enterpriseAverage || null;
  return (
    <Shell overline="Enterprise average" title={comparisons?.org?.name || ctx.activeOrg?.name || 'All Sites'}>
      {compLoading && <Spinner />}
      {!compLoading && !avg && <Alert severity="info">No survey data yet for this organization — enterprise REPS scores appear once responses come in.</Alert>}
      {!compLoading && avg && <ScoreTiles scores={avg} thresholds={thresholds} big />}
    </Shell>
  );
}

/* ------------------------------------------------------------------ alerts & divergences */

function RiskAlertsWidget({ size }) {
  const ctx = useRoleDashboard();
  const navigate = useNavigate();
  const riskAlerts = ctx?.riskAlerts || [];
  const healthMessages = ctx?.healthMessages || [];
  const items = [
    ...healthMessages.filter((m) => m.severity === 'critical' || m.severity === 'warning').slice(0, 3).map((m) => ({ id: `hm_${m.id}`, severity: m.severity, title: m.title, summary: m.summary })),
    ...riskAlerts.map((a) => ({ id: `ra_${a.id}`, severity: a.severity, title: a.title, summary: a.summary || a.description || '' })),
  ].sort((a, b) => (a.severity === 'critical' ? -1 : 0) - (b.severity === 'critical' ? -1 : 0));
  const rows = items.slice(0, size === 'M' ? 3 : size === 'L' ? 6 : 12);
  return (
    <Shell title="Top Critical Alerts" action={<Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`${ROOT}/ia/health-messages`)}>All alerts</Button>}>
      <Stack spacing={1.5} divider={<Divider />}>
        {rows.map((alert) => (
          <Box key={alert.id}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label={(alert.severity || '').toUpperCase()} size="small" color={alert.severity === 'critical' ? 'error' : 'warning'} />
              <Typography variant="body2" fontWeight={600}>{alert.title}</Typography>
            </Box>
            {size !== 'M' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{alert.summary}</Typography>}
          </Box>
        ))}
        {items.length === 0 && <EmptyState text="No critical alerts right now." />}
      </Stack>
    </Shell>
  );
}

function DivergencesWidget() {
  const ctx = useRoleDashboard();
  if (!ctx) return null;
  const { healthMessages, activeOrg, comparisons } = ctx;
  const divergences = healthMessages.filter((m) => m.quadrant && m.deltas && m.deltas[m.quadrant]);
  const siteNameFor = (msg) =>
    activeOrg?.sites?.find((s) => s.id === msg.scope?.siteId)?.name ||
    comparisons?.siteScores?.find((s) => s.siteId === msg.scope?.siteId)?.siteName ||
    activeOrg?.name ||
    'Organization';
  return (
    <Shell title="Survey vs Actual Divergences">
      {divergences.length === 0 && <Alert severity="info">No divergence alerts — survey scores and field data currently agree.</Alert>}
      <Stack spacing={1.5} divider={<Divider />}>
        {divergences.map((m) => {
          const d = m.deltas[m.quadrant];
          return (
            <Box key={m.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>{siteNameFor(m)}</Typography>
                <Chip label={`${m.quadrant} • Δ${d.delta}`} size="small" color={d.direction === 'aligned' ? 'success' : 'warning'} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Survey {d.survey} • Actual {d.actual} — {m.summary}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Shell>
  );
}

/* ------------------------------------------------------------------ report shortcuts */

function ReportShortcutsWidget() {
  const navigate = useNavigate();
  const items = [
    { label: 'Quick Reports', to: `${ROOT}/reports` },
    { label: 'Report Builder', to: `${ROOT}/reports?tab=builder` },
    { label: 'Warning History', to: `${ROOT}/reports?tab=warnings` },
    { label: 'Reports on Demand', to: `${ROOT}/ia/reports-on-demand` },
  ];
  return (
    <Shell title="Reports">
      <Stack spacing={1}>
        {items.map((it) => (
          <Button key={it.label} variant="outlined" size="small" startIcon={<ReportIcon />} onClick={() => navigate(it.to)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
            {it.label}
          </Button>
        ))}
      </Stack>
    </Shell>
  );
}

/* ------------------------------------------------------------------ registration */

const DEFS = [
  { id: 'my-submissions', title: 'My Recent Submissions', description: 'The OE Conditions you logged, newest first, with severity and status.', category: 'oe_conditions', component: MySubmissionsWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', href: `${ROOT}/rollup/trl-entry`, dashboards: [K.drewItm, K.drewFls], frameless: true },
  { id: 'my-nwt-scores', title: 'My NWT', description: 'REPS scores of your Natural Work Team from the last 14 days of responses, with review counts.', category: 'scores', component: MyNwtScoresWidget, sizes: ['M', 'L'], defaultSize: 'L', dashboards: [K.drewItm, K.drewFls], frameless: true },
  { id: 'my-site-scores', title: 'My Site', description: 'REPS scores of your site from its latest snapshot.', category: 'scores', component: MySiteScoresWidget, sizes: ['M', 'W', 'L'], defaultSize: 'W', href: `${ROOT}/rollup/site`, dashboards: [K.drewItm, K.drewFls, K.drewDh], frameless: true },
  { id: 'dept-scores', title: 'Department REPS Scores', description: 'Headcount-weighted REPS scores of your department (crews weigh equally).', category: 'scores', component: DeptScoresWidget, sizes: ['W', 'L'], defaultSize: 'W', href: `${ROOT}/rollup/site`, dashboards: [K.drewDh], minRole: 'department_lead', frameless: true },
  { id: 'nwts-in-dept', title: 'NWTs in My Department', description: 'Every Natural Work Team in your department with its REPS scores and response basis.', category: 'scores', component: NwtsInDeptWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', dashboards: [K.drewDh], minRole: 'department_lead', frameless: true },
  { id: 'l1-queue', title: 'L1 Review Queue', description: 'OE Conditions from your NWT awaiting your L1 review.', category: 'oe_conditions', component: L1QueueWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', href: `${ROOT}/rollup/review-queue`, dashboards: [K.drewFls, K.drewDh, K.drewOe], minRole: 'team_lead', frameless: true },
  { id: 'l2-queue', title: 'L2/DH Approval Queue', description: 'Items from your NWTs awaiting your severity verification and priority assignment.', category: 'oe_conditions', component: L2QueueWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', href: `${ROOT}/rollup/severity-calibration`, dashboards: [K.drewDh, K.drewOe], minRole: 'department_lead', frameless: true },
  { id: 'comparisons', title: 'Comparisons', description: 'NWT-to-NWT for a department head; REPS by site for executives — opens the Comparisons Engine.', category: 'scores', component: ComparisonsWidget, sizes: ['L', 'XL'], defaultSize: 'L', href: `${ROOT}/ia/comparisons`, dashboards: [K.drewDh, K.drewExec, K.drewOe], minRole: 'department_lead', frameless: true },
  { id: 'sites-glance', title: 'Sites at a Glance', description: 'Every site with its average score, trend arrow and response basis.', category: 'scores', component: SitesGlanceWidget, sizes: ['M', 'L'], defaultSize: 'L', href: `${ROOT}/rollup/enterprise`, dashboards: [K.drewExec, K.drewOe], minRole: 'site_manager', frameless: true },
  { id: 'enterprise-scores', title: 'Enterprise REPS', description: 'Headcount-weighted enterprise average per quadrant.', category: 'scores', component: EnterpriseScoresWidget, sizes: ['W', 'L'], defaultSize: 'W', href: `${ROOT}/rollup/enterprise`, dashboards: [K.drewExec], minRole: 'site_manager', frameless: true },
  { id: 'risk-alerts', title: 'Top Critical Alerts', description: 'Critical and warning health messages plus document-analysis risk alerts.', category: 'alerts', component: RiskAlertsWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', href: `${ROOT}/ia/health-messages`, dashboards: [K.drewFls, K.drewDh, K.drewExec], frameless: true },
  { id: 'divergences', title: 'Survey vs Actual', description: 'Where survey scores and field data disagree (Divergence Detection).', category: 'alerts', component: DivergencesWidget, sizes: ['M', 'L', 'XL'], defaultSize: 'L', href: `${ROOT}/ia/comparisons`, dashboards: [K.drewExec], minRole: 'site_manager', frameless: true },
  { id: 'report-shortcuts', title: 'Reports', description: 'Quick Reports, Report Builder, Warning History and Reports on Demand.', category: 'guidance', component: ReportShortcutsWidget, sizes: ['S', 'M'], defaultSize: 'M', href: `${ROOT}/reports`, dashboards: [K.drewDh, K.drewExec, K.drewOe], minRole: 'department_lead', frameless: true },
];

for (const def of DEFS) widgetRegistry.upsert(def);

export const ROLE_DASHBOARD_WIDGET_IDS = DEFS.map((d) => d.id);
